import { ColumnDef } from '@tanstack/react-table';
import { Cloud, Upload, X, Check } from 'lucide-react';
import { StatBox } from '../../../../../components/ui/stat-box';
import { ProgressCircle } from '../../../../../components/ui/progress-circle';
import { UploadBox } from '../../../../../components/ui/upload-box';
import { DataTable } from '../../../../../components/ui/data-table';
import { FileStatusItem } from '../../../../../api/services/liquidationService';

interface LiquidationUploadSectionProps {
  docType: string;
  extensionFile: string;
  uploadTitle: string;
  dbName: string;
  fileStatusData: FileStatusItem[];
  /** Upload aceito, mas o arquivo ainda não apareceu em processed_files */
  aguardandoProcessamento?: boolean;
  onUploadComplete: () => void;
  calculateErrorPercentage: () => number;
  calculateSuccessPercentage: () => number;
  getErrorCount: () => number;
  getSuccessCount: () => number;
}

const columns: ColumnDef<FileStatusItem>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    enableHiding: true,
  },
  {
    accessorKey: 'created_by',
    header: 'Usuário',
  },
  {
    accessorKey: 'size',
    header: 'Tamanho',
    cell: ({ row }) => {
      const bytes = row.getValue('size') as number;
      const mb = (bytes / (1024 * 1024)).toFixed(2);
      return `${mb} MB`;
    },
  },
  {
    accessorKey: 'real_name',
    header: 'Nome do arquivo',
  },
  {
    accessorKey: 'doc_type',
    header: 'Tipo do documento',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string;
      const statusColors = {
        success: 'text-green-600',
        error: 'text-red-600',
        processing: 'text-yellow-600',
      };
      return (
        <span className={statusColors[status as keyof typeof statusColors] || 'text-gray-600'}>
          {status}
        </span>
      );
    },
  },
  {
    accessorKey: 'error',
    header: 'Mensagem',
  },
];

export function LiquidationUploadSection({
  docType,
  extensionFile,
  uploadTitle,
  dbName,
  fileStatusData,
  aguardandoProcessamento = false,
  onUploadComplete,
  calculateErrorPercentage,
  calculateSuccessPercentage,
  getErrorCount,
  getSuccessCount,
}: LiquidationUploadSectionProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <UploadBox
          title={uploadTitle}
          docType={docType}
          allowedExtensions={extensionFile}
          dbName={dbName}
          icon={<Cloud className="h-6 w-6" />}
          onUploadComplete={onUploadComplete}
        />

        <StatBox
          title="Total de arquivos recebidos hoje"
          value={fileStatusData.length}
          icon={<Upload className="h-6 w-6 text-primary" />}
        />

        <ProgressCircle
          title="Arquivos com Erro"
          value={getErrorCount()}
          percentage={calculateErrorPercentage()}
          icon={<X className="h-6 w-6 text-destructive" />}
        />

        <ProgressCircle
          title="Arquivos processados"
          value={getSuccessCount()}
          percentage={calculateSuccessPercentage()}
          icon={<Check className="h-6 w-6 text-green-600" />}
        />
      </div>

      {fileStatusData.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Status dos Arquivos</h2>
          <DataTable
            columns={columns}
            data={fileStatusData}
            searchableColumns={['real_name']}
            searchPlaceholder="Buscar por nome do arquivo..."
          />
        </div>
      )}

      {fileStatusData.length === 0 &&
        (aguardandoProcessamento ? (
          <div className="text-center py-8 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            <p className="text-muted-foreground">
              Arquivo enviado. Aguardando entrar na fila de processamento...
            </p>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Nenhum arquivo processado hoje para este tipo de documento.
            </p>
          </div>
        ))}
    </div>
  );
}
